import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Shield, Check, AlertTriangle, Ban, Eye, Users } from 'lucide-react';
import { Link } from 'react-router-dom';

const CommunityGuidelinesModal = ({ isOpen, onAccepted, onDeclined }) => {
  const [acceptedGuidelines, setAcceptedGuidelines] = useState(false);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setScrolledToBottom(true);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-lg max-h-[90vh]" hideClose>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-6 w-6 text-emerald-500" />
            GEMS Community Guidelines
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-lg p-3">
            <p className="text-emerald-300 text-sm">
              To ensure a safe and enjoyable experience for everyone, please review and accept our Community Guidelines.
            </p>
          </div>
          
          <ScrollArea 
            className="h-64 border rounded-lg p-4 bg-gray-900"
            onScroll={handleScroll}
          >
            <div className="text-sm text-gray-300 space-y-4">
              <div className="flex items-start gap-3">
                <Ban className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-red-400">No Sexual Content</strong>
                  <p className="text-gray-400">Nudity, sexually suggestive content, and inappropriate behavior are strictly prohibited during streams.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-purple-400">Minor Safety</strong>
                  <p className="text-gray-400">Users must be 18+. No minors in streams. Any content exploiting children is immediately reported to authorities.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-orange-400">No Violence or Dangerous Acts</strong>
                  <p className="text-gray-400">Violence, threats, self-harm content, dangerous stunts, and weapons are prohibited.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <svg className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
                </svg>
                <div>
                  <strong className="text-yellow-400">No Hate Speech</strong>
                  <p className="text-gray-400">Discrimination, harassment, bullying, and hate speech based on any characteristic are not tolerated.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <svg className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm6.31-3.1L7.1 5.69C8.45 4.63 10.15 4 12 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z"/>
                </svg>
                <div>
                  <strong className="text-red-400">No Illegal Activities</strong>
                  <p className="text-gray-400">Drug use, underage drinking, fraud, scams, and any illegal activities are prohibited.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Eye className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-emerald-400">Streaming Standards</strong>
                  <p className="text-gray-400">Maintain appropriate attire, safe environments, sobriety, and moderate language during streams.</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <strong className="text-blue-400">Fair Play</strong>
                  <p className="text-gray-400">No cheating, collusion, exploits, bots, or multi-accounting. Play fair!</p>
                </div>
              </div>
              
              <div className="bg-amber-900/30 border border-amber-500/30 rounded-lg p-3 mt-4">
                <p className="text-amber-300 text-sm">
                  <strong>⚠️ AI Moderation:</strong> Our AI bots monitor streams 24/7. Violations can result in 
                  automatic warnings, stream termination, temporary suspension, or permanent bans depending on severity.
                </p>
              </div>
              
              <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3">
                <p className="text-red-300 text-sm">
                  <strong>🚨 Zero Tolerance:</strong> Sexual content and child exploitation result in 
                  immediate permanent ban and reporting to authorities.
                </p>
              </div>
            </div>
          </ScrollArea>
          
          <div className="space-y-3">
            <label className="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-800 transition-colors">
              <input
                type="checkbox"
                checked={acceptedGuidelines}
                onChange={(e) => setAcceptedGuidelines(e.target.checked)}
                className="mt-0.5 h-5 w-5 text-emerald-600 rounded"
              />
              <span className="text-sm text-gray-300">
                I have read and agree to follow the{' '}
                <Link to="/community-guidelines" target="_blank" className="text-emerald-400 hover:underline font-medium">
                  GEMS Community Guidelines
                </Link>
                . I understand that violations may result in account restrictions or termination.
              </span>
            </label>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onDeclined}
              className="flex-1"
            >
              Decline
            </Button>
            <Button
              onClick={onAccepted}
              disabled={!acceptedGuidelines}
              className={`flex-1 ${acceptedGuidelines ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-600'}`}
            >
              <Check className="mr-2 h-4 w-4" />
              I Agree
            </Button>
          </div>
          
          <p className="text-xs text-gray-500 text-center">
            These guidelines help keep our community safe. Thank you for being part of GEMS!
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CommunityGuidelinesModal;
